import type { SchemaTypes } from '@allesauto/graphql';
import { PaymentType, ProductType } from '@allesauto/graphql';
import {
  FinanceCalculatorOptions,
  LeasingCalculationInput,
  LeasingOptions,
  LoanCalculationInput,
  LoanOptions,
} from '@allesauto/graphql/src/mock/schema';
import axios from 'axios';
import dateFormat from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import startOfDay from 'date-fns/startOfDay';
import { Maybe } from 'graphql/jsutils/Maybe';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isObjectLike from 'lodash/isObjectLike';
import omit from 'lodash/omit';
import { NextRouter } from 'next/router';
import { FormatNumberOptions, IntlShape } from 'react-intl';
import slugify from 'slugify';
import { RouteFilter } from 'web/src/features/PCP/types';
import {
  WL_INTEGRATION_SLUG_AUTOHAUS_SCHWARTE,
  WL_INTEGRATION_SLUG_BOB_AUTOMOBILE,
  WL_INTEGRATION_SLUG_BRAASCH_GRUPPE,
  WL_INTEGRATION_WINTER_LAUSITZ,
} from 'web/src/features/whitelabel/util/getWhiteLabelIntegrationSlugs';

import {
  CALCULATE_LEASING_PCP,
  CALCULATE_LOAN_PCP,
  consts,
  FinanceTypeEn,
  FuelType,
  NumberOfBlockedDays,
  PdpParams,
  ROUTES,
  StepperStep,
  SupportedCurrencies,
  VEHICLE_BY_ID,
} from './consts';
import { Route, routeLocales } from './routes';
import { FieldTypeLink } from './storyblok';
import { WhiteLabelConfig } from '../../allesauto-core/src/lib/whiteLabel/WhiteLabelContext';

export interface AxiosFinanceCalculatorResponse {
  data: {
    calculateLoan: LoanCalculatorResponse;
    calculateLeasing: LeasingCalculatorResponse;
  };
}

interface LoanCalculatorResponse {
  summary: string;
}

interface LeasingCalculatorResponse {
  summary: string;
}

export interface SaveFilterToRedux {
  key: 'reduxFilter';
  routeFilter: RouteFilter;
  urlFilter: Record<string, unknown>;
}

export interface DefaultFilter {
  key: 'filter';
  routeFilter: RouteFilter;
  createVehicleObjectFilter: SchemaTypes.VehicleFilterOptions;
}

export type OpenGraphType = {
  siteName: string;
  description: string;
  title?: string;
  logo?: string;
};

type Option = {
  value: string | number;
  label: string | number;
};

type Match = {
  route: Route;
  locale: keyof Route['paths'];
};

const routes: Route[] = Object.values(ROUTES);

export function openGraph({
  siteName,
  title,
  description,
  logo = '/large-og-1200x630.png',
}: OpenGraphType): string {
  const ogLogo = encodeURIComponent(logo);
  const ogSiteName = encodeURIComponent(siteName.trim());
  const ogTemplateTitle = title ? encodeURIComponent(title.trim()) : undefined;
  const ogDesc = encodeURIComponent(description.trim());
  return `/${ogLogo}?siteName=${ogSiteName}&description=${ogDesc}${
    ogTemplateTitle ? `&templateTitle=${ogTemplateTitle}` : ''
  }`;
}

export function addClass(identifier: string, className: string) {
  document.querySelector(identifier)?.classList.add(className);
}

export function removeClass(identifier: string, className: string) {
  document.querySelector(identifier)?.classList.remove(className);
}

export const getPathname = (
  pathObj: { [key: string]: string },
  locale: string | undefined | false,
  replacements?: {
    [key: string]: string | number;
  }
): string => {
  if (typeof locale !== 'string' || typeof pathObj[locale] !== 'string') {
    throw new RangeError(`Unknown page ${pathObj} for locale ${locale}`);
  }

  let result = pathObj[locale];

  if (replacements !== null && replacements !== undefined) {
    for (const [key, value] of Object.entries(replacements)) {
      result = result.replace(new RegExp(`[${key}]`, 'igm'), value.toString());
    }
  }

  return result;
};

export const generateRandomString = function () {
  return Math.random().toString(20).substring(2, 8);
};

export const createUUID = function () {
  let d = new Date().getTime();
  let d2 =
    (typeof performance !== 'undefined' &&
      performance.now &&
      performance.now() * 1000) ||
    0;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16;
    if (d > 0) {
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

interface FormatPriceOptions extends FormatNumberOptions {
  withFractionDigits?: boolean;
}

/**
 * Formats a price using intl.formatNumber but with all required options defined and without a space before a currency sign.
 */
export function formatPrice(
  intl: IntlShape,
  price?: number | null,
  options?: FormatPriceOptions
) {
  if (isNil(price)) {
    return '';
  }

  const formatNumberOptions: FormatNumberOptions = {
    style: 'currency',
    currency: SupportedCurrencies.DEFAULT,
    ...(options?.withFractionDigits
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
    ...options,
  };

  return intl.formatNumber(price, formatNumberOptions);
}

export const formatNumberInputForBackend = (value: string) => {
  return value && value.replace(/\./g, '').replace(/,/g, '.');
};

export const formatNumberInputPriceFromBackend = (value: string) => {
  return value && value.replace(/\./g, ',');
};

export const formatNumberInputWithDotsAndComma = (
  value: string,
  hasDots: boolean,
  hasDecimals: boolean
) => {
  let resultRegex = '';
  const numberPattern = /\D/g;
  const dotPattern = /\./g;
  const threeNumberSetPattern = /(\d)(?=(\d{3})+(?!\d))/g;
  const numberWithCommaPattern = /[^0-9,]/g;
  const numberWithoutCommaAtTheBeginingPattern = /^,/g;
  const numberWithoutDuplicateCommaPattern = /,(?=,)/g;
  const twoNumbersDecimalPattern = /(\.\d{1,2}).*/g;
  const commaPattern = /,/g;
  const emptyReplacement = '';
  const dotReplacement = '.';
  const commaReplacement = ',';
  const dotAfterThreeNumberSet = '$1.';
  const emptyAfterTwoNumberDecimalsReplacement = '$1';

  if (hasDots && !hasDecimals && value) {
    resultRegex = value
      .replace(numberPattern, emptyReplacement)
      .replace(dotPattern, emptyReplacement)
      .replace(threeNumberSetPattern, dotAfterThreeNumberSet);
  }
  if (hasDecimals && value) {
    resultRegex = value
      .replace(numberWithCommaPattern, emptyReplacement)
      .replace(numberWithoutCommaAtTheBeginingPattern, emptyReplacement)
      .replace(numberWithoutDuplicateCommaPattern, emptyReplacement)
      .replace(dotPattern, emptyReplacement)
      .replace(commaPattern, dotReplacement)
      .replace(twoNumbersDecimalPattern, emptyAfterTwoNumberDecimalsReplacement)
      .replace(dotPattern, commaReplacement);
  }
  if (hasDots && hasDecimals && resultRegex !== '' && value) {
    resultRegex = resultRegex.replace(
      threeNumberSetPattern,
      dotAfterThreeNumberSet
    );
  }
  return resultRegex;
};

export const parseFormattedNumber = (
  intl: IntlShape,
  value: string
): number => {
  const tmp = intl.formatNumber(1000);

  let thousandsSeparator: string | RegExp = '';
  if (tmp.indexOf('.') !== -1) thousandsSeparator = /\./g;
  if (tmp.indexOf(',') !== -1) thousandsSeparator = /,/g;

  return Number(value.replace(thousandsSeparator, '').replace(',', '.'));
};

export const toUrlJSON = (
  data: any,
  encode = false,
  filterKeyNames?: string[]
): string | boolean => {
  let res: string | boolean = false;

  try {
    res = JSON.stringify(data, (key, value) => {
      if (
        value !== null &&
        value !== undefined &&
        (filterKeyNames || []).indexOf(key) === -1
      ) {
        return value;
      }
      return undefined;
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }

  if (Object.keys(res).length === 0) res = false;

  return encode ? encodeURIComponent(res) : res;
};

export const fromUrlJSON = (data: string, decode = false): any => {
  let res = {};

  try {
    res = JSON.parse(decode ? decodeURIComponent(data) : data);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }

  return res;
};

export const isEmptyObject = (obj: any): boolean => {
  if (isEmpty(obj) && isNaN(obj)) return true;
  if (obj === null || obj === undefined) return true;
  if (!isObjectLike(obj)) return false;

  const propCount = Object.keys(obj).length;
  let emptyCount = 0;

  for (let index = 0; index < Object.keys(obj).length; index++) {
    const keyName = Object.keys(obj)[index];
    const value = obj[keyName];
    if (value === undefined || value === null) emptyCount++;
  }

  return emptyCount === propCount;
};

export const createMarkup = (htmlContent: string) => {
  return { __html: htmlContent };
};

export const loremIpsumGenerator = (numberOfLines: number) => {
  return Array.from(Array(numberOfLines).keys()).map(
    () =>
      `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nam lorem nisl
      faucibus id lectus ac, ullamcorper lacinia mauris. Praesent facilisis
      euismod tincidunt. Integer malesuada enim ligula, nec finibus nisl
      convallis sed. Sed ante sapien, sollicitudin a turpis id, dignissim rutrum
      nisi. Pellentesque rhoncus, nulla sit amet gravida commodo, dolor tortor
      ultricies odio, vel gravida ante diam in turpis. Etiam a sagittis ipsum.
      Proin sodales tortor vitae sem vulputate faucibus. Integer posuere non
      lacus in auctor. Orci varius natoque penatibus et magnis dis parturient
      montes, nascetur ridiculus mus.`
  );
};

export const checkoutSumPrice = (products: any) =>
  products.reduce((acc: number, curr: any) => acc + curr.value, 0);

/**
 * getSlug - Slugify string via https://github.com/simov/slugify
 *
 * @param data
 * @returns string
 */
export const getSlug = (data = '') => {
  const options = {
    // replacement: '-',  // replace spaces with replacement character, defaults to `-`
    // remove: undefined, // remove characters that match regex, defaults to `undefined`
    lower: true, // convert to lower case, defaults to `false`
    // strict: false,     // strip special characters except replacement, defaults to `false`
    // locale: 'vi',       // language code of the locale to use
    // trim: true         // trim leading and trailing replacement chars, defaults to `true`
  };
  return slugify(data, options);
};

export const getTokenHeader = (token: string) =>
  token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : null;

export const getAnonymousHeader = (UUID: string) =>
  UUID
    ? {
        'X-Anonymous-Customer-Unique-Id': UUID,
      }
    : null;

export const getWhiteLabelMerchantOriginHeader = (origin: string) =>
  origin
    ? {
        'white-label-merchant-origin': origin,
      }
    : null;

export const sortBillingAddressList = (list: SchemaTypes.CustomerAddress[]) =>
  list &&
  list.sort(
    (currAddress, nextAddress) =>
      Number(nextAddress.isDefaultBilling) -
      Number(currAddress.isDefaultBilling)
  );

export const findSameAddress = (
  addresses: SchemaTypes.CustomerAddress[],
  address: SchemaTypes.CustomerAddress
): SchemaTypes.CustomerAddress | undefined => {
  const omitUniqueFields = (address: SchemaTypes.CustomerAddress) => {
    return omit(address, [
      'id',
      'isDefaultBilling',
      'isDefaultShipping',
      'geoCoordinates',
      '__typename',
    ]);
  };

  address = omitUniqueFields(address);

  return addresses.find((a) => isEqual(omitUniqueFields(a), address));
};

export const findSameAddressById = (
  addresses: SchemaTypes.CustomerAddress[],
  idAddress: string
): SchemaTypes.CustomerAddress | undefined => {
  return addresses.find((a) => a.id === idAddress);
};

/**
 * getSteppersCurrentStep - Given the page route and an array
 * of StepperSteps, returns the current step for that page
 *
 * @param route - The current page route
 * @param steps - Array of StepperSteps
 *
 * @returns StepperStep
 */
export const getSteppersCurrentStep = (
  route: string,
  steps: StepperStep[]
): StepperStep => {
  const selected = steps.find((step) => {
    return step.links.some((link) => route.includes(link));
  });
  return selected ? selected : steps[0];
};

/**
 * Checks if a string is a special character,
 * a special character is any character other than a digit or a number
 * @param value - The value to be checked
 * @returns boolean
 */
export const isSpecialCharacter = (value: string) => {
  const reg = RegExp('^[^A-Za-z0-9]{1}$');
  return reg.exec(value);
};

export const getColumnsAndMaxTiles = (
  nbTiles: number,
  maxNumberTiles: number
) => {
  let nbColumns;
  let maxTiles = maxNumberTiles;

  if (nbTiles % 3 === 0) {
    nbColumns = 3;
  } else if (nbTiles % 2 === 0) {
    nbColumns = 2;
  } else if (nbTiles > 4) {
    maxTiles = 4;
    nbColumns = 2;
  } else {
    maxTiles = 0;
  }

  return { nbColumns, maxTiles };
};

export const getCurrentYear = () => {
  const d = new Date();
  return d.getFullYear();
};

export const generateRangeOfNumbers = (start: number, stop: number) => {
  return Array.from({ length: stop - start + 1 }, (_, i) => start + i)
    .map((number) => {
      return { value: number, label: number };
    })
    .reverse();
};

/**
 * getFilteredMakes - Filter an array of makes by the preferred makes
 * available on consts
 *
 * @param makes - The list of makes to filter
 *
 * @returns list of makes without the preferred makes
 */
export const getFilteredMakes = (makes: SchemaTypes.Make[]) => {
  if (consts.PREFERRED_MAKES.length === 0 || makes.length === 0) {
    return [];
  }

  const preferredMakesArray = consts.PREFERRED_MAKES.map((make) => make.name);

  return makes.filter((m) => {
    let makeFound = false;
    for (let i = 0; i < preferredMakesArray.length; i++) {
      if (m.name === preferredMakesArray[i]) {
        makeFound = true;
      }
    }
    if (!makeFound) return m.name;
  });
};

/**
 * getFilteredMakes - Function that returns difference between
 * current date and a selected date
 *
 * @param dateSelected - Selected date to compare
 *
 * @returns a number that represent difference between dates if a date was selected
 */
export const getYearsFromCurrentDate = (dateSelected: Date) => {
  if (!dateSelected) {
    return;
  }
  const currentDate = new Date();
  const timeDiffYear = currentDate.getTime() - dateSelected.getTime();
  const numberOfDays = timeDiffYear / (1000 * 3600 * 24);

  return Math.abs(numberOfDays / 365);
};

export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

//TODO: Should use internationalised when GW/BE decide Date format
export function formatDateFormsRequest(value: Date) {
  if (!value) {
    return;
  }
  return dateFormat(value, 'yyyy-MM-dd');
}

// Formats date to yyyy-MM-dd (keeps same day even if at edge of different timezones)
export function formatDateToIsoDate(value: Date | null) {
  if (!value) {
    return null;
  }

  const d = startOfDay(new Date(value));
  return formatISO(d, { representation: 'date' });
}

/**
 * Checks if the given hostname contains an integration slug (subdomain).
 * If so, the function returns it, else `undefined`.
 *
 * @deprecated
 */
export const getIntegrationSlugFromHostName = (
  hostname: string
): string | undefined => {
  const parts = hostname.replace(/:.*/, '').split('.');

  // test for subdomain on local dev server (supported in most browsers; tested on Chrome, Firefox, Edge)
  const isLocalDev = parts.length === 2 && parts[1] === 'localhost';

  // TODO Check whether this is still needed after AAA-5218
  // test for subdomain on root-360 environments
  const isRoot360 =
    parts.length === 4 &&
    parts[2] === 'root360-service' &&
    parts[3] === 'cloud';

  // test for subdomain on alles.auto preview environments
  const isPreview =
    parts.length === 4 &&
    parts[0] !== 'shop' &&
    parts[0] !== 'www' &&
    (parts[1] === 'dev' || parts[1] === 'uat' || parts[1] === 'stage') &&
    parts[2] === 'alles' &&
    parts[3] === 'auto';

  // test for subdomain on production domain with 3 segments
  const isProd3Part =
    parts.length === 3 &&
    parts[0] !== 'shop' &&
    parts[0] !== 'www' &&
    parts[1] === 'alles' &&
    parts[2] === 'auto';

  // test for subdomain on production domain with 4 segments
  const isProd4Part =
    parts.length === 4 &&
    parts[0] !== 'www' &&
    parts[1] === 'shop' &&
    parts[2] === 'alles' &&
    parts[3] === 'auto';

  return isLocalDev || isRoot360 || isPreview || isProd3Part || isProd4Part
    ? parts[0]
    : undefined;
};

/**
 * Checks if the hostname in the HTTP request contains an integration
 * slug (subdomain or domain). If so, the function returns it, else `undefined`.
 *
 * @example
 * - shop.opel-sieber-automobile.de => opel-sieber-automobile-de
 * - shop.dev.opel-sieber-automobile.de => opel-sieber-automobile-de
 * - opel-sieber-automobile.de.localhost:3000 => opel-sieber-automobile-de
 * - opel-sieber-automobile.allesauto-shop-dev.root360-service.cloud => opel-sieber-automobile-de
 * - www.autohauseuropa.de => autohauseuropa-de
 */
export const getIntegrationSlugFromHost = (
  hostname: string
): string | undefined => {
  const localhostRe = /(\.?localhost)?(:3\d{3})*/g; // https://regexr.com/77ljp
  const shopSuffixRe = /(\.allesauto-shop-dev)|(\.root360-service\.cloud)?/gi; // https://regexr.com/777ta
  const shopPrefixRe = /(shop\.)((dev|uat|stage|test)\.)?/i; // https://regexr.com/775hu
  const shopPrefixRef = /(zubehoer\.)((dev|uat|stage|test)\.)?/i; // https://regexr.com/775hu
  const localhostMatch = hostname.match(localhostRe);

  // 1. Return if hostname is exactly localhost
  if (localhostMatch?.[1] === 'localhost') {
    return;
  }

  // 2. Remove shop suffix, i.e. root360-service.cloud
  hostname = hostname.replace(shopSuffixRe, '');

  // 3. Test for domain or subdomain on alles.auto (all environments)
  const isAaDomain =
    hostname.includes('alles.auto') || hostname.includes('allesauto');

  if (isAaDomain) {
    return;
  }

  // 4. Remove shop and env from URL
  hostname = hostname.replace(shopPrefixRe, '');
  hostname = hostname.replace(shopPrefixRef, '');

  // 5. Remove localhost and port (only dev env)
  const isLocalhost = localhostRe.test(hostname);
  if (isLocalhost) {
    hostname = hostname.replace(localhostRe, '');
  }

  // 6. Reformat to slug (with dashes)
  return hostname.replace(/\./g, '-');
};

export const getFinancePerCarDetails = (
  financingDetails: SchemaTypes.FinancingDetail[],
  financeCalculatorOptions:
    | SchemaTypes.FinanceCalculatorOptions
    | null
    | undefined,
  paymentType: SchemaTypes.PaymentType | null | undefined
): {
  summaryText: string | undefined;
  monthlyPrice: number | undefined;
  paymentSelected: PaymentType | null | undefined;
  legalText: string | undefined;
} => {
  const financeVehicle =
    (Array.isArray(financingDetails) &&
      financingDetails.find((finance) => finance?.type === paymentType)) ||
    undefined;

  const cheaperFinance =
    (Array.isArray(financingDetails) &&
      financingDetails?.length > 0 &&
      financingDetails.reduce((prev, curr) =>
        (prev?.monthlyRate || 0) < (curr?.monthlyRate || 0) ? prev : curr
      )) ||
    undefined;

  const isCashRangeType = paymentType === PaymentType.CashRange;

  const getfinanceCalculatorOptionsLegalText = (
    paymentSelectedType: SchemaTypes.PaymentType | null | undefined
  ) => {
    if (
      PaymentType.LeasingRateRange === paymentSelectedType &&
      financeCalculatorOptions &&
      financeCalculatorOptions.leasing
    ) {
      return financeCalculatorOptions.leasing.legalText;
    }
    if (
      PaymentType.LoanRateRange === paymentSelectedType &&
      financeCalculatorOptions &&
      financeCalculatorOptions.loan
    ) {
      return financeCalculatorOptions.loan.legalText;
    }
    return '';
  };

  if (isCashRangeType || paymentType === undefined) {
    if (cheaperFinance) {
      return {
        summaryText: cheaperFinance.summary || '',
        monthlyPrice: cheaperFinance.monthlyRate,
        paymentSelected: cheaperFinance.type || PaymentType.LoanRateRange,
        legalText: getfinanceCalculatorOptionsLegalText(
          cheaperFinance.type || PaymentType.CashRange
        ),
      };
    }
  } else {
    if (financeVehicle) {
      return {
        summaryText: financeVehicle.summary || '',
        monthlyPrice: financeVehicle.monthlyRate,
        paymentSelected: paymentType,
        legalText: getfinanceCalculatorOptionsLegalText(paymentType),
      };
    }
  }

  return {
    summaryText: undefined,
    monthlyPrice: undefined,
    paymentSelected: undefined,
    legalText: undefined,
  };
};

/**
 * Return list of days
 *
 * @param localeName - name of local, f.e. de-DE, default en-US
 * @param weekday - formart of weekday, default long
 * @returns array of days
 */
export function getDaysForLocale(
  localeName = 'en-US',
  weekday: 'long' | 'short' | 'narrow' = 'long'
) {
  const format = new Intl.DateTimeFormat(localeName, { weekday }).format;

  return Array.from(Array(7).keys()).map((day) =>
    format(new Date(Date.UTC(2021, 5, day)))
  );
}

/**
 * Return minimum date for a dealer appointment
 *
 * @param selectedDate - currently selected day
 * @param daysToAddManually - number of days to add beetwen the selected and minimum dates
 * @param isTradeIn - is the trade-in form
 * @param addDaysAutomatically - add days automatically instead of adding 'daysToAddManually'
 * @param skipWholeWeekend - sets if a whole weekend (Sat & Sun) is skipped during auto days addition or just Sundays
 * @param wlMerchantOrigin - for custom date for for specified merchant
 * @returns a minimum possible date for an appointment
 */
export function getDealerAppointmentMinDate(
  selectedDate = new Date(),
  daysToAddManually = 4,
  isTradeIn = false,
  addDaysAutomatically = false,
  skipWholeWeekend = false,
  wlMerchantOrigin?: string
) {
  const monthDay = selectedDate.getDate(); // 1-31
  const weekDay = selectedDate.getDay(); // 0-6, from 0 - sunday, 6 - saturday

  if (!addDaysAutomatically) {
    daysToAddManually =
      weekDay === 6 || (isTradeIn && weekDay === 3) // is sat or (trade-in & wed)
        ? daysToAddManually + 1
        : daysToAddManually;

    return new Date(selectedDate.setDate(monthDay + daysToAddManually));
  }

  let daysToAdd: number;

  if (!skipWholeWeekend) {
    daysToAdd =
      weekDay !== 6 // is not sat
        ? wlMerchantOrigin && isNowagInstance(wlMerchantOrigin)
          ? NumberOfBlockedDays.NOWAG_EXCLUDING_SUNDAY_WORKING_DAYS
          : NumberOfBlockedDays.EXCLUDING_SUNDAY_WORKING_DAYS
        : wlMerchantOrigin && isNowagInstance(wlMerchantOrigin)
        ? NumberOfBlockedDays.NOWAG_INCLUDING_SUNDAY_WORKING_DAYS
        : NumberOfBlockedDays.EXCLUDING_SUNDAY;
  } else {
    daysToAdd =
      weekDay !== 5 && weekDay !== 4 // is not fri & thu
        ? wlMerchantOrigin && isNowagInstance(wlMerchantOrigin)
          ? NumberOfBlockedDays.NOWAG_EXCLUDING_FULL_WEEKEND_WORKING_DAYS
          : NumberOfBlockedDays.EXCLUDING_FULL_WEEKEND_WORKING_DAYS
        : wlMerchantOrigin && isNowagInstance(wlMerchantOrigin)
        ? NumberOfBlockedDays.NOWAG_INCLUDING_FULL_WEEKEND
        : NumberOfBlockedDays.EXCLUDING_FULL_WEEKEND;

    weekDay === 6 && ++daysToAdd; // is sat
  }

  return new Date(selectedDate.setDate(monthDay + daysToAdd));
}

export const getFormattedDeliveryDate = (
  deliveryDate?: string | null
): string => {
  if (!deliveryDate) {
    return 'Sofort';
  }

  const deliveryDateObj = new Date(deliveryDate);
  deliveryDateObj.setHours(0, 0, 0, 0); // Reset time to compare only dates

  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  // If deliveryDate is in the past we show 'Sofort'
  if (deliveryDateObj < currentDate) {
    return 'Sofort';
  }

  // Format deliveryDate into "DD-MM-YYYY"
  const day = deliveryDateObj.getDate().toString().padStart(2, '0');
  const month = (deliveryDateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = deliveryDateObj.getFullYear();
  return `${day}.${month}.${year}`;
};

export const verifyVehicleDate = (registeredDate: string | undefined) => {
  const validVehicleDateRegex = new RegExp(/((0[1-9]|1[0-2])\/[12]\d{3})/);
  if (registeredDate && validVehicleDateRegex.test(registeredDate)) {
    return registeredDate;
  }
  return '-';
};

export const generateOptionArrayOfYears = (min: number) => {
  const max = new Date().getFullYear();
  const years: Option[] = [];

  for (let i = max; i >= min; i--) {
    years.push({ value: i, label: i });
  }
  return years;
};

export const generateOptionArrayOfMonths = () => {
  const months: Option[] = [];
  for (let i = 1; i <= 12; i++) {
    const month = (i < 10 ? '0' : '') + i;
    months.push({ value: month, label: month });
  }
  return months;
};

export const getFinanceMonthlyText = (paymentType: PaymentType | undefined) => {
  switch (paymentType) {
    case PaymentType.LeasingRateRange:
      return 'ui.HEADER.MINI_SHOPPING_CART.MONTHLY_LEASING_RATE';
    case PaymentType.LoanRateRange:
      return 'ui.HEADER.MINI_SHOPPING_CART.MONTHLY_LOAN_RATE';
    default:
      return undefined;
  }
};

export function getAccessoriesDealerProviderId(
  vehicleCartItem: SchemaTypes.CartItem
) {
  if (vehicleCartItem.productType !== ProductType.Vehicle) {
    return '';
  }

  const { makeName } = vehicleCartItem.product as SchemaTypes.Vehicle;

  const provider = vehicleCartItem.dealer.accessoriesProviders?.find(
    (ap) => ap.makeName?.toLowerCase() === makeName?.toLowerCase()
  );

  return provider?.providerId ?? '';
}

export function getAccessoriesDealerProviderIdCarPdp(
  vehiclePdpItem: SchemaTypes.Vehicle
) {
  const productType =
    ProductType.Vehicle.charAt(0).toUpperCase() +
    ProductType.Vehicle.slice(1).toLowerCase();

  if (vehiclePdpItem.__typename !== productType) {
    return '';
  }

  const { makeName } = vehiclePdpItem as SchemaTypes.Vehicle;

  const provider = vehiclePdpItem.accessoryProviders?.find(
    (ap) => ap.carBrand?.toLowerCase() === makeName?.toLowerCase()
  );

  return provider?.dataSourceId ?? '';
}

export function getAccessoriesDealerProviderIdGarage(
  vehicleGarageItem: SchemaTypes.GarageVehicle | undefined
) {
  if (vehicleGarageItem?.__typename !== 'GarageVehicle') {
    return '';
  }

  const { make } = vehicleGarageItem;

  const provider = vehicleGarageItem.dealer?.accessoriesProviders?.find(
    (ap) => ap.makeName?.toLowerCase() === make?.toLowerCase()
  );

  return provider?.providerId ?? '';
}

export const getFueltypeTranslationKey = (
  fuelType: SchemaTypes.Maybe<string> | undefined
) => {
  switch (fuelType) {
    case FuelType.HYDROGEN:
    case FuelType.GAS:
    case FuelType.NATURAL_GAS:
      return 'ui.PDP.ENVIRONMENT.FUEL_CONSUMPTION_KG';
    case FuelType.ELECTRIC:
      return 'ui.PDP.ENVIRONMENT.FUEL_CONSUMPTION_KWH';
    default:
      return 'ui.PDP.ENVIRONMENT.FUEL_CONSUMPTION_L';
  }
};

/**
 * Get parsed consumption value - Needed because hydrogen vehicles consumption value comes in a different format
 */
export function getFuelConsumptionValue(
  intl: IntlShape,
  fuelType: FuelType | Maybe<string> | undefined,
  consumption: number
) {
  if (fuelType === FuelType.HYDROGEN) {
    // Fuel consumption value for Hydrogen vehicles comes in grams and we
    // need to display it in Kilograms
    return intl.formatNumber(consumption / 1000);
  }
  return +consumption;
}

export const createLinkForRouting = (
  extraOverlayParam: FinanceTypeEn,
  pageName: string,
  params: PdpParams
) => {
  let returnUrl = pageName !== undefined ? pageName : '';

  if (params) {
    for (const val of Object.values(params)) {
      if (val !== undefined) returnUrl += val;
    }

    if (extraOverlayParam === FinanceTypeEn.EMPTY) {
      return returnUrl;
    } else {
      return returnUrl + '/?financeType=' + extraOverlayParam;
    }
  }
  return returnUrl;
};

export const isProtectedRoute = (pathName: string) => {
  const routes = Object.values(ROUTES);
  const comparedPathName =
    pathName.length > 1 && pathName.endsWith('/')
      ? pathName.substring(0, pathName.length - 1)
      : pathName;
  const currentRoute = routes.find((route) =>
    route.hasSlug
      ? comparedPathName.startsWith(route.paths.de)
      : comparedPathName === route.paths.de
  );

  return Boolean(currentRoute?.rules);
};

export const getCarConfiguratorParamsRouterQuery = (
  query: NextRouter['query']
) => {
  const { configuratorId, dealerId } = query;
  let auxConfiguratorId: string | undefined, auxDealerId: string | undefined;

  if (configuratorId && typeof configuratorId === 'string') {
    auxConfiguratorId = configuratorId;
  }

  if (dealerId && typeof dealerId === 'string') {
    auxDealerId = dealerId;
  }
  return {
    configuratorId: auxConfiguratorId,
    configuratorDealerId: auxDealerId,
  };
};

export const isExternalUrl = (href = '') => {
  const regex = new RegExp('^(https|http)?://(.*)');

  return regex.test(href);
};

/**
 * Remove environment folder from url
 * - `pages`for dev
 * - `pages-prod`for prod
 */
export const trimEnvironmentFolder = (url: string) => {
  if (url.startsWith('/')) url = url.slice(1);
  const splittedUrl = url.split('/');
  //const trimPagesFolder = splittedUrl[0] === process.env.STORYBLOK_PAGE_FOLDER;
  const trimPagesFolder = false;
  const trimmedUrl = '/' + splittedUrl.slice(trimPagesFolder ? 1 : 0).join('/');
  return parseSlashToURL(trimmedUrl);
};

export const getUrlFromStoryblokLink = (link: FieldTypeLink) => {
  if (link === undefined) return '';
  const url = link.url || link.cached_url;
  if (!url) return '';
  const cleanedUrl = fixPagesUrl(url);
  if (isExternalUrl(cleanedUrl)) return cleanedUrl;
  return trimEnvironmentFolder(cleanedUrl);
};

export const parseSlashToURL = (url: string) => {
  if (url.endsWith('/') || url.startsWith('#') || url.includes('?')) return url;

  return url + '/';
};

export const isDateBeforeCurrentMonth = (date: Date) => {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const comparedMonth = date.getMonth();
  const comparedYear = date.getFullYear();

  return comparedMonth < currentMonth || comparedYear < currentYear;
};

export const isVideo = (value: string) => {
  const reg = new RegExp(/\.(mp4|mov|avi|mkv|flv|webm)$/i);
  return reg.exec(value);
};

/***
 *  result date = 28 Dez 2023
 */
export const formatDateToShortGermanDate = (dateString: string) => {
  if (dateString === '' || undefined) {
    return;
  }
  const date = new Date(dateString);
  const dateObject = new Intl.DateTimeFormat('de', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'Europe/Berlin',
  });
  return dateObject.format(date).replace(/. /g, ' ');
};

export const checkSubGroups = (
  modelDropdownData: SchemaTypes.Model[],
  selectedDropdownModelName: string | undefined
) => {
  const model =
    modelDropdownData &&
    modelDropdownData.find((m) => m.name === selectedDropdownModelName);
  return model && model.subGroups && model.subGroups.length > 0;
};

export const isAllesAutoDomain = (url: string) => {
  const domains = [
    'allesauto-shop-dev.root360-service.cloud',
    'shop.uat.alles.auto',
    'alles.auto',
  ];

  const [hostname] = url.split('://')[1].split('/');

  return domains.some((domain) => hostname.includes(domain));
};

export const matchRoute = (pathname: string): Match | undefined => {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    // strip trailing slash
    pathname = pathname.substring(0, pathname.length - 1);
  }

  //TODO remove this when BE will provide new urls for bank request
  const pattern = /\/account\/orders\/([0-9]+)\/banking-request\/([0-9]+)/;
  const ids = pattern.exec(pathname);

  if (ids && ids.length > 2) {
    const match: Match = {
      route: ROUTES.BANKING_REQUEST_OLD,
      locale: 'de',
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: Object is possibly 'null'.
    match.route.rules.ifIsOld.target = `${ROUTES.BANKING_REQUEST_PERSONAL.paths.de}/${ids[2]}`;

    return match;
  }

  let match: Match | undefined;

  for (const route of routes) {
    for (const routeLocale of routeLocales) {
      const path = route.paths[routeLocale];
      if (!route.hasSlug && pathname === path) {
        match = {
          route,
          locale: routeLocale,
        };
        break;
      } else if (route.hasSlug && pathname.startsWith(path)) {
        match = {
          route,
          locale: routeLocale,
        };
        continue; // keep looking for an exact match
      }
    }
  }

  return match;
};

export const fixPagesUrl = (url: string, currentUrl?: string) => {
  const isPreview = url.includes('preview/pages');
  const currentHref =
    typeof window !== 'undefined' ? window.location.href : currentUrl;
  const isPreviewRunning = currentHref?.includes('preview/pages');

  if (
    ((isExternalUrl(url) && isAllesAutoDomain(url)) || !isExternalUrl(url)) &&
    !isPreview
  ) {
    const isUAT = process.env.STORYBLOK_PAGE_FOLDER === 'pages-uat';
    const shouldRedirectProd =
      url.includes('pages/') && isUAT && !isExternalUrl(url);

    if (shouldRedirectProd) {
      const newUrl = new URL(url, 'https://alles.auto');
      return newUrl.href;
    }

    const cleanedUrl = url.replace('pages/', '').replace('pages-uat/', '');
    const match = isExternalUrl(url) || matchRoute(url);

    if (isPreviewRunning && !match) {
      let newUrl: string;

      if (cleanedUrl.startsWith('/')) {
        newUrl = `/preview/pages${cleanedUrl}`;
      } else {
        newUrl = `preview/pages/${cleanedUrl}`;
      }

      return newUrl;
    }

    return cleanedUrl;
  }

  return url;
};

export function isCheckOutProcess(url: string): boolean {
  return (
    url.includes(ROUTES.CHECKOUT.paths.de) ||
    url.includes(ROUTES.CHECKOUT.paths.en) ||
    url.includes(ROUTES.CHECKOUT_BILLING.paths.de) ||
    url.includes(ROUTES.CHECKOUT_BILLING.paths.de) ||
    url.includes(ROUTES.CHECKOUT_PAYMENT.paths.de) ||
    url.includes(ROUTES.CHECKOUT_PAYMENT.paths.en) ||
    url.includes(ROUTES.CHECKOUT_DELIVERY.paths.de) ||
    url.includes(ROUTES.CHECKOUT_DELIVERY.paths.de) ||
    url.includes(ROUTES.CHECKOUT_CONFIRMATION.paths.de) ||
    url.includes(ROUTES.CHECKOUT_CONFIRMATION.paths.en)
  );
}

export function getHostFromWhitelabelConfig(
  whiteLabelConfig?: WhiteLabelConfig
) {
  if (whiteLabelConfig) {
    let host = whiteLabelConfig.shopUrl;
    if (host.endsWith('/')) {
      host = host.slice(0, -1);
    }
    return host;
  }
}

export async function getFinanceCalculationSummary(
  vehicleId: string,
  selectedPayment: PaymentType
): Promise<string | undefined> {
  const URL = String(process.env.APOLLO_CLIENT_URL);

  const {
    data: {
      data: { vehicleById },
    },
  } = await axios.post(URL, {
    query: VEHICLE_BY_ID,
    variables: { vehicleId },
  });

  if (!vehicleById) {
    return undefined;
  }

  const financeCalculatorOptions: FinanceCalculatorOptions =
    vehicleById.financeCalculatorOptions;
  const isLoan = isLoanOption(selectedPayment);

  const loanCalculationInput: LoanCalculationInput = {
    downPayment:
      getFinancingType(financeCalculatorOptions)?.downPayment.default ?? 0,
    duration: getFinancingType(financeCalculatorOptions)?.duration.default ?? 0,
    mileage: getFinancingType(financeCalculatorOptions)?.mileage.default ?? 0,
    finalInstallment:
      (getFinancingType(financeCalculatorOptions) as LoanOptions)
        ?.finalInstallment.default ?? 0,
    hasFinalInstallment: true,
  };

  const leasingCalculationInput: LeasingCalculationInput = {
    downPayment:
      getFinancingType(financeCalculatorOptions)?.downPayment.default ?? 0,
    duration: getFinancingType(financeCalculatorOptions)?.duration.default ?? 0,
    mileage: getFinancingType(financeCalculatorOptions)?.mileage.default ?? 0,
  };

  if (
    financeCalculatorOptions.loan !== null ||
    financeCalculatorOptions.leasing !== null
  ) {
    try {
      const {
        data: { data },
      } = await axios.post<AxiosFinanceCalculatorResponse>(URL, {
        query: isLoan ? CALCULATE_LOAN_PCP : CALCULATE_LEASING_PCP,
        variables: {
          vehicleId,
          input: isLoan ? loanCalculationInput : leasingCalculationInput,
        },
      });

      let summary;

      if (isLoan) {
        summary = data.calculateLoan.summary;
      } else {
        summary = data.calculateLeasing.summary;
      }

      return summary;
    } catch (e) {
      return undefined;
    }
  }
}

export function getFinancingType(
  calculatorOptions: FinanceCalculatorOptions
): LoanOptions | LeasingOptions | undefined {
  if (!calculatorOptions) return undefined;
  return calculatorOptions.loan
    ? calculatorOptions.loan
    : (calculatorOptions.leasing as LeasingOptions);
}

function isLoanOption(paymentType: PaymentType): boolean {
  return paymentType === PaymentType.LoanRateRange;
}

export function isWhiteLabel(data: any): boolean {
  return !!data;
}

export function getSpecificWhiteLabelMerchant(
  wlConfig: WhiteLabelConfig | undefined,
  staticOrigin: string
) {
  return wlConfig?.merchantOrigin.includes(staticOrigin);
}

export function isWltpAttributeAvailable(attribute: string): boolean {
  if (attribute === 'null') {
    return false;
  } else if (+attribute < 0) {
    return false;
  }

  return +attribute > 0;
}

export function isAuregoShop(
  whiteLabel: WhiteLabelConfig | undefined
): boolean {
  return whiteLabel?.merchantOrigin === 'aurego-de';
}

export function isWinterLausitzShop(
  whiteLabel: WhiteLabelConfig | undefined
): boolean {
  return whiteLabel?.merchantOrigin === 'winter-lausitz-de';
}

export function isSeitzAutohaus(
  whiteLabel: WhiteLabelConfig | undefined
): boolean {
  return whiteLabel?.merchantOrigin === 'seitz-autohaus-de';
}

export function getIsIncluded(value: string, key: string): boolean {
  return value.includes(key);
}

export function getWhiteLabelHost(host: string): { slug: string } {
  let slug: string;
  const unFormattedHostUrl = host.split(':')[0];

  if (unFormattedHostUrl.includes('localhost')) {
    const sliceEndIndex = unFormattedHostUrl.search('.localhost');
    slug = unFormattedHostUrl.slice(0, sliceEndIndex).split('.').join('-');
  } else if (
    unFormattedHostUrl.includes('shop.uat') ||
    unFormattedHostUrl.includes('shop.dev')
  ) {
    slug = unFormattedHostUrl.split('.').slice(2).join('-');
  } else if (unFormattedHostUrl.includes('shop')) {
    slug = unFormattedHostUrl.split('.').slice(1).join('-');
  } else {
    slug = '';
  }

  return { slug };
}

export function getWhiteLabelCtaTeaserDescription(
  whiteLabel: WhiteLabelConfig
): string {
  return whiteLabel?.merchantOrigin === WL_INTEGRATION_SLUG_BOB_AUTOMOBILE
    ? 'ui.TRADE_IN.PDP.DESCRIPTION_WL_BOB'
    : 'ui.TRADE_IN.PDP.DESCRIPTION_WL';
}

export const isRueschampWhiteLabel = (merchantOrigin?: string) => {
  return merchantOrigin === 'autohaus-rueschkamp-de';
};

export const isWinterInstance = (merchantOrigin?: string) => {
  return merchantOrigin === WL_INTEGRATION_WINTER_LAUSITZ;
};

export const getDefaultPriceRangeForWinter = () => ({
  key: PaymentType.LoanRateRange,
  max: 788,
  min: 205,
});

export const getPCPFilter = (
  filter: SaveFilterToRedux | DefaultFilter,
  prices?: SchemaTypes.PriceRange[],
  merchantOrigin?: string
) => {
  const price = prices?.find((p) => p.key === PaymentType.LoanRateRange);
  let priceRange: SchemaTypes.PriceRange;

  if (isWinterInstance(merchantOrigin)) {
    if (price) {
      priceRange = {
        key: price.key,
        max: price.max,
        min: price.min,
      };
    } else {
      priceRange = getDefaultPriceRangeForWinter();
    }

    return {
      ...filter,
      priceRange,
    };
  }

  return filter;
};

export const isNowagInstance = (merchantOrigin: string): boolean => {
  return [
    WL_INTEGRATION_SLUG_AUTOHAUS_SCHWARTE,
    WL_INTEGRATION_SLUG_BRAASCH_GRUPPE,
  ].includes(merchantOrigin);
};

export const getDefaultNowagDate = (): Date => {
  const date = new Date();
  date.setDate(
    date.getDate() + NumberOfBlockedDays.NOWAG_EXCLUDING_SUNDAY_WORKING_DAYS
  );

  return date;
};

export const getAccessoryCarouselVehicleData = ({
  vehicleCartItem,
  vehicleGarageItem,
  vehiclePdpItem,
}: {
  vehicleCartItem?: SchemaTypes.CartItem;
  vehicleGarageItem?: SchemaTypes.GarageVehicle;
  vehiclePdpItem?: SchemaTypes.Vehicle;
}) => {
  const vehicleVin =
    (vehicleCartItem?.product as SchemaTypes.Vehicle)?.vin ||
    vehicleGarageItem?.vin ||
    vehiclePdpItem?.vin;

  const vehicleSeries =
    (vehicleCartItem?.product as SchemaTypes.Vehicle)?.modelName ||
    vehicleGarageItem?.model ||
    vehiclePdpItem?.modelName;

  const makeName =
    (vehicleCartItem?.product as SchemaTypes.Vehicle)?.makeName ||
    vehicleGarageItem?.make ||
    vehiclePdpItem?.makeName;

  // replace old accessoriesProviders with new property accessoryProviders for car PDP carousel
  if (
    vehiclePdpItem?.vehicleDetail?.dealer &&
    vehiclePdpItem?.accessoryProviders
  ) {
    const updatedDealer = {
      ...vehiclePdpItem.vehicleDetail.dealer,
      accessoriesProviders: vehiclePdpItem.accessoryProviders.map(
        (provider) => ({
          makeName: provider.carBrand ?? '',
          providerId: provider.dataSourceId ?? '',
        })
      ),
    };

    const updatedVehicleDetail = {
      ...vehiclePdpItem.vehicleDetail,
      dealer: updatedDealer,
    };

    vehiclePdpItem = {
      ...vehiclePdpItem,
      vehicleDetail: updatedVehicleDetail,
    };
  }

  const dealer =
    vehicleCartItem?.dealer ||
    vehicleGarageItem?.dealer ||
    vehiclePdpItem?.vehicleDetail?.dealer;

  let accessoriesDealerProviderId = '';

  if (vehicleCartItem && vehicleCartItem.product) {
    accessoriesDealerProviderId =
      getAccessoriesDealerProviderId(vehicleCartItem);
  } else if (vehicleGarageItem) {
    accessoriesDealerProviderId =
      getAccessoriesDealerProviderIdGarage(vehicleGarageItem);
  } else if (vehiclePdpItem) {
    accessoriesDealerProviderId =
      getAccessoriesDealerProviderIdCarPdp(vehiclePdpItem);
  }

  return {
    accessoriesDealerProviderId,
    vehicleVin: vehicleVin || undefined,
    vehicleSeries: vehicleSeries || undefined,
    makeName: makeName || undefined,
    dealer: dealer || undefined,
  };
};
